import { useImperativeHandle, useMemo, useRef, useState } from "react";

import { SelectChangeEvent } from "@mui/material";

import { UseSelectParams } from "./Select.types";

const getDefaultState = <V>(value?: V, defaultValue?: V): V => {
  if (typeof value !== "undefined") {
    return value;
  }
  if (typeof defaultValue !== "undefined") {
    return defaultValue;
  }
  return undefined as unknown as V;
};

export const useSelect = <V, R>({
  ref,
  defaultValue,
  value,
  onChange,
}: UseSelectParams<V, R>) => {
  const selectRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<V>(getDefaultState(value, defaultValue));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useImperativeHandle(
    ref,
    (() => {
      return {
        instance: selectRef.current,
        value: state,
        setValue: (v: V | undefined) => {
          setState(v as unknown as V);
        },
        reset: () => {
          setState(undefined as unknown as V);
        },
      };
    }) as unknown as () => R,
    [state]
  );

  const onChangeHandler = (event: SelectChangeEvent<V>) => {
    const newValue = event.target.value as unknown as V;
    if (typeof value === "undefined") {
      setState(newValue);
    }
    onChange?.(newValue);
  };

  const onClearStateHandler = () => {
    // if (typeof value === "undefined") {
    setState(undefined as unknown as V);
    // }
    onChange?.(undefined as unknown as V);
  };

  const valueState = useMemo(
    () => (typeof value !== "undefined" ? value : state),
    [value, state]
  );

  return {
    selectRef,
    valueState,
    onChangeHandler,
    onClearStateHandler,
  };
};
